import { MenuIcon } from "./icons";
import type { MenuId } from "./view-model";

export interface MenuBarProps {
  friendBadge: number;
  /** The picture book (or a visit from it) is open: the Book button is pressed and closes it. */
  bookOpen?: boolean;
  /** The Monday sequence is playing: the book stays shut until it ends or is skipped. */
  bookDisabled?: boolean;
  /** The Trophies sheet is open: the Trophies button is pressed. */
  trophiesOpen?: boolean;
  /** The Trophies button is off during the Monday sequence and in the book. */
  trophiesDisabled?: boolean;
  /** Items edit mode is active: the Items button is pressed. */
  editing?: boolean;
  /** The Items button is off during the Monday sequence and outside the game screen. */
  itemsDisabled?: boolean;
  /** The More sheet is open: the More button is pressed. */
  moreOpen?: boolean;
  /** The More button is off during the Monday sequence and in the book. */
  moreDisabled?: boolean;
  onMenu(id: MenuId): void;
}

export function MenuBar({
  friendBadge,
  bookOpen = false,
  bookDisabled = false,
  trophiesOpen = false,
  trophiesDisabled = false,
  editing = false,
  itemsDisabled = false,
  moreOpen = false,
  moreDisabled = false,
  onMenu,
}: MenuBarProps) {
  const badgeCount = Math.max(0, Math.floor(friendBadge));
  const moreAria =
    badgeCount > 0 ? `More, ${badgeCount} new requests` : "More";

  return (
    <nav className="appbar" aria-label="Menu">
      <button
        type="button"
        className="ab"
        id="bBook"
        data-testid="menu-book"
        aria-label="My picture book"
        aria-pressed={bookOpen}
        disabled={bookDisabled}
        onClick={() => onMenu("book")}
      >
        <MenuIcon id="book" />
        Book
      </button>
      <button
        type="button"
        className="ab"
        id="bTrophies"
        data-testid="menu-trophies"
        aria-label="Trophies"
        aria-pressed={trophiesOpen}
        disabled={trophiesDisabled}
        onClick={() => onMenu("trophies")}
      >
        <MenuIcon id="trophies" />
        Trophies
      </button>
      <button
        type="button"
        className="ab"
        id="bItems"
        data-testid="menu-items"
        aria-label="Edit items"
        aria-pressed={editing}
        disabled={itemsDisabled}
        onClick={() => onMenu("items")}
      >
        <MenuIcon id="items" />
        Items
      </button>
      <button
        type="button"
        className="ab"
        id="bMore"
        data-testid="menu-more"
        aria-label={moreAria}
        aria-pressed={moreOpen}
        disabled={moreDisabled}
        onClick={() => onMenu("more")}
      >
        <MenuIcon id="more" />
        More
        {badgeCount > 0 ? (
          <span className="badge" id="badge" data-testid="friend-badge">
            {badgeCount}
          </span>
        ) : null}
      </button>
    </nav>
  );
}
